<script setup>
import { useI18n } from "vue-i18n";
const { t, tm } = useI18n();

import { Swiper, SwiperSlide } from "swiper/vue";
import { Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";

import { onBeforeUnmount, onMounted, ref } from "vue";

const isSmallScreen = ref(window.innerWidth <= 922);

const updateScreenSize = () => {
  isSmallScreen.value = window.innerWidth <= 922;
};

onMounted(() => {
  window.addEventListener("resize", updateScreenSize);
  updateScreenSize();
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", updateScreenSize);
});

const modules = [Pagination];
</script>

<template>
  <div class="orderNow-wrapper">
    <div class="orderNow-container">
      <h1 id="orderNow-title">{{ t("new_customer.howItWorks.header") }}</h1>
      <swiper
        v-if="isSmallScreen"
        :spaceBetween="20"
        :slidesPerView="1"
        :pagination="{ clickable: true }"
        :modules="modules"
        class="swiper-container"
      >
        <swiper-slide
          class="orderNow-slide"
          v-for="(step, key) in tm('new_customer.howItWorks.steps')"
          :key="key"
        >
          <div class="orderNow-item">
            <div class="icon">
              <img :src="require(`../assets/icons/${step.icon}`)" alt="icon" />
            </div>
            <div class="text">
              <h2 v-html="step.title" class="text-title"></h2>
              <p v-html="step.description" class="text-description"></p>
            </div>
          </div>
        </swiper-slide>
      </swiper>

      <div v-else class="items-container">
        <div
          class="item"
          v-for="(step, key) in tm('new_customer.howItWorks.steps')"
          :key="key"
        >
          <div class="icon">
            <img :src="require(`../assets/icons/${step.icon}`)" alt="icon" />
          </div>
          <div class="text">
            <h2>{{ step.title }}</h2>
            <p v-html="step.description"></p>
          </div>
        </div>
      </div>
      <a :href="t('new_customer.howItWorks.link')" class="btn">{{
        t("new_customer.howItWorks.button")
      }}</a>
    </div>
  </div>
</template>

<style scoped>
.orderNow-wrapper {
  background: #f6c243;
}

.orderNow-container {
  padding: 58px 0 76px;
  max-width: 1293px;
  width: 100%;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
}

.items-container {
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
  margin: 0 auto;
  justify-content: center;
}

.item,
.orderNow-item {
  flex: 1 1 calc(50% - 20px);
  border-radius: 10px;
  background: #fcfcfc;
  padding: 25px 0;
  max-width: 499px;
  max-height: 315px;
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow: hidden;
  position: relative;
  gap: 7px;
  text-align: center;
}

.icon {
  max-width: 95px;
  max-height: 80px;
  margin-bottom: 10px;
}

.icon img {
  width: 100%;
  height: auto;
}

.text {
  display: flex;
  flex-direction: column;
  max-width: 415px;
  text-wrap: pretty;
}

.btn {
  z-index: 10;
  cursor: pointer;
  position: relative;
  display: block;
  overflow: hidden;
  max-width: 242px;
  border-radius: 110px;
  border: 3px solid #2a1e13;
  margin: 30px auto;
  font-family: "JETSansVF2", Arial, sans-serif;
  font-weight: 900;
  padding: 15px 58px 15px 58px;
  text-align: center;
  background-color: #f6c243;
}

a {
  text-decoration: none;
  color: #2a1e13;
}

.swiper-container {
  width: 100%;
}

:deep(.swiper-pagination) {
  display: flex;
  justify-content: center;
  margin-top: 40px;
  position: relative;
  gap: 5px;
}

:deep(.swiper-pagination-bullet) {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  border: 3px solid #2a1e13;
  transition: background-color 0.3s;
  background-color: #f6c243;
}

:deep(.swiper-pagination-bullet-active) {
  background-color: #242e30;
}

@media only screen and (max-width: 768px) {
  .orderNow-container {
    width: 100%;
    display: inline-block;
    flex-direction: row;
  }

  #orderNow-title {
    font-size: 24px;
    line-height: 26px;
    text-align: center;
    margin-bottom: 30px;
  }

  .text-title {
    font-size: 16px;
    line-height: 22px;
  }

  .text-description {
    font-size: 14px;
    line-height: 19px;
  }

  .orderNow-slide {
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .orderNow-item {
    gap: 6px;
    padding: 26px 5px 19px;
    max-width: 238px;
    max-height: 273px;
  }

  .icon {
    max-width: 44px;
    max-height: 44px;
  }

  .btn {
    max-width: 232px;
    max-height: 43px;
    padding: 10px 53px;
    border: 2px solid #2a1e13;
  }
}

@media only screen and (max-width: 992px) and (min-width: 769px) {
  .orderNow-container {
    display: inline-block;
    margin-top: 20px;
    max-width: 100%;
  }

  #orderNow-title {
    text-align: center;
  }

  .orderNow-item {
    padding: 25px 15px;
  }

  .orderNow-slide {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
  }

  .items-container {
    padding: 0 10px;
    gap: 16px;
  }
}

@media only screen and (max-width: 1200px) and (min-width: 993px) {
  .items-container {
    margin: 30px;
  }
}
</style>
